import { useCallback } from 'react'

import { type ApprovalMode } from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { ApproverCandidate } from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import { useTranslation } from 'react-i18next'

import { chakra } from '@enechain/ecloud-designsystem'

import { ApprovalMatrixModalHost } from '~/pages/AdminApprovalFlow/components/ApprovalMatrixModalHost'
import { AreaHeaderCell } from '~/pages/AdminApprovalFlow/components/MatrixHeaders'
import { MatrixRow } from '~/pages/AdminApprovalFlow/components/MatrixRow'
import {
  getModeOptions,
  matrixTableStyle,
} from '~/pages/AdminApprovalFlow/components/matrixStyles'
import {
  AREA_CODE_LABELS,
  type CellKey,
  type ProcessDef,
} from '~/pages/AdminApprovalFlow/constants/processMap'
import type {
  MatrixAction,
  ProcessState,
} from '~/pages/AdminApprovalFlow/models/reducer'

type Props = {
  processDef: ProcessDef
  processState: ProcessState
  candidates: ApproverCandidate[]
  dispatch: (action: MatrixAction) => void
  processLabel: string
  /**
   * 「自動」セルの中身を描く (AC-APF-037: 2 行サマリ + 「ルール編集」)。
   * ルール本体・bid-limits 実効値・編集モーダルは `useAutoBidSection` が持ち、
   * マトリクスはこの 1 本だけを受け取って各セルへ配る。
   */
  renderAutoBidCell?: (area: AreaCode) => React.ReactNode
  /**
   * 「保存」を押した後にエラー表示にするセル (AC-APF-071)。 どのセルを数えるかは
   * `ProcessAccordion` が Alert の件数と同じ判定で決める。
   */
  saveErrorKeys?: ReadonlySet<CellKey>
}

export const ApprovalMatrix = ({
  processDef,
  processState,
  candidates,
  dispatch,
  processLabel,
  renderAutoBidCell,
  saveErrorKeys,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const MODE_OPTIONS = getModeOptions(t)

  const handleModeChange = useCallback(
    (key: CellKey, mode: ApprovalMode) => {
      dispatch({
        type: 'SET_CELL_MODE',
        businessProcess: processDef.businessProcess,
        key,
        mode,
      })
    },
    [dispatch, processDef.businessProcess],
  )

  return (
    <ApprovalMatrixModalHost
      candidates={candidates}
      dispatch={dispatch}
      processDef={processDef}
      processLabel={processLabel}
      processState={processState}
    >
      {(modalControls) => (
        <chakra.div overflowX="auto">
          <table style={matrixTableStyle}>
            <thead>
              <tr>
                {processDef.areas.map((area) => (
                  <AreaHeaderCell
                    key={area}
                    areaLabel={AREA_CODE_LABELS[area]}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              <MatrixRow
                candidates={candidates}
                modalControls={modalControls}
                modeOptions={MODE_OPTIONS}
                onModeChange={handleModeChange}
                processDef={processDef}
                processState={processState}
                renderAutoBidCell={renderAutoBidCell}
                saveErrorKeys={saveErrorKeys}
              />
            </tbody>
          </table>
        </chakra.div>
      )}
    </ApprovalMatrixModalHost>
  )
}
